import React, {useEffect, useState} from "react";
import {DragDropContext, Droppable, Draggable} from "@hello-pangea/dnd";
import {Input} from "../components/input";
import {Button} from "../components/Button";
import api from "../api/axiosInstance";

export default function CategoryAdminPage() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newCategory, setNewCategory] = useState("");
    const [newSubcategory, setNewSubcategory] = useState("");
    const [reorderedIds, setReorderedIds] = useState(new Set());

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/category");
            const grouped = res.data.reduce((acc, item) => {
                if (!acc[item.category]) acc[item.category] = [];
                acc[item.category].push(item);
                return acc;
            }, {});

            setCategories(
                Object.entries(grouped).map(([category, subs]) => {
                    const sorted = subs
                        .sort((a, b) => a.order - b.order)
                        .map((sub, index) => ({
                            ...sub,
                            order: index,
                            savedOrder: index,
                        }));

                    return {category, subcategories: sorted};
                })
            );

            setReorderedIds(new Set());
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchCategories();
    }, []);

    const handleDragEnd = (result, categoryIndex) => {
        if (!result.destination) return;

        const newCategories = categories.map((cat) => ({
            ...cat,
            subcategories: cat.subcategories.map((s) => ({...s})),
        }));

        const subcats = newCategories[categoryIndex].subcategories;
        const [moved] = subcats.splice(result.source.index, 1);
        subcats.splice(result.destination.index, 0, moved);

        const changedIds = new Set(reorderedIds);

        subcats.forEach((sub, idx) => {
            sub.order = idx;
            if (sub.savedOrder !== idx) {
                changedIds.add(sub.subcategory);
            } else {
                changedIds.delete(sub.subcategory);
            }
        });

        newCategories[categoryIndex].subcategories = subcats;
        setCategories(newCategories);
        setReorderedIds(changedIds);
    };


    const handleSaveOrder = async () => {
        try {
            for (const cat of categories) {
                const updates = cat.subcategories.filter((sub) =>
                    reorderedIds.has(sub.subcategory)
                );

                if (updates.length === 0) continue;

                await Promise.all(
                    updates.map((sub) =>
                        api.put("/admin/category", {
                            category: sub.category,
                            subcategory: sub.subcategory,
                            order: sub.order, // correct new order
                        })
                    )
                );
            }

            await fetchCategories();
        } catch (err) {
            console.error("Error saving order:", err);
        }
    };


    const handleAddSubcategory = async () => {
        if (!newCategory || !newSubcategory) return;
        try {
            await api.post("/admin/category", {
                category: newCategory,
                subcategory: newSubcategory,
                order:
                    categories.find((c) => c.category === newCategory)?.subcategories
                        .length || 0,
                isActive: true,
            });
            setNewSubcategory("");
            fetchCategories();
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteSubcategory = async (category, subcategory) => {
        if (!window.confirm(`Delete ${subcategory}?`)) return;
        try {
            await api.delete("/admin/category", {data: {category, subcategory}});
            fetchCategories();
        } catch (err) {
            console.error(err);
        }
    };

    const handleToggleActive = async (subcat) => {
        try {
            await api.put("/admin/category", {
                category: subcat.category,
                subcategory: subcat.subcategory,
                isActive: !subcat.isActive,
            });
            fetchCategories();
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <div>Loading...</div>;

    const buttonStyle = {
        maxWidth: "80px",
        padding: "0.25rem 0.5rem",
        fontSize: "0.875rem",
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl font-bold">Category Admin</h1>
                <Button
                    style={buttonStyle}
                    onClick={handleSaveOrder}
                    disabled={reorderedIds.size === 0}
                >
                    Save Order
                </Button>
            </div>

            <div className="mb-6 flex gap-2">
                <Input
                    placeholder="Category"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                />
                <Input
                    placeholder="Subcategory"
                    value={newSubcategory}
                    onChange={(e) => setNewSubcategory(e.target.value)}
                />
                <Button style={buttonStyle} onClick={handleAddSubcategory}>
                    Add
                </Button>
            </div>

            {categories.map((cat, categoryIndex) => (
                <div key={cat.category} className="mb-6 border rounded p-4 bg-white shadow">
                    <h2 className="text-xl font-semibold mb-2">{cat.category}</h2>
                    <DragDropContext
                        onDragEnd={(result) => handleDragEnd(result, categoryIndex)}
                    >
                        <Droppable droppableId={cat.category}>
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    {cat.subcategories.map((subcat, idx) => (
                                        <Draggable
                                            key={subcat.subcategory}
                                            draggableId={`${cat.category}-${subcat.subcategory}`}
                                            index={idx}
                                        >
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className={`flex justify-between items-center p-2 mb-1 border rounded ${
                                                        reorderedIds.has(subcat.subcategory)
                                                            ? "bg-yellow-100"
                                                            : "bg-gray-50"
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-2">
                            <span>
                              {subcat.subcategory} (order: {subcat.order})
                            </span>
                                                        <label className="flex items-center gap-1">
                                                            <input
                                                                type="checkbox"
                                                                checked={subcat.isActive}
                                                                onChange={() => handleToggleActive(subcat)}
                                                            />
                                                            Active
                                                        </label>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        style={buttonStyle}
                                                        onClick={() =>
                                                            handleDeleteSubcategory(
                                                                subcat.category,
                                                                subcat.subcategory
                                                            )
                                                        }
                                                    >
                                                        Delete
                                                    </Button>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            ))}
        </div>
    );
}
